import React, { useState } from 'react';
import { GoChevronDown, GoChevronLeft, GoChevronRight } from 'react-icons/go';
import { FluidContainer, InputDate, Loader } from '../../../components';
import type { PlayerWeeklyDayData, PlayerWeeklyActivityData } from '../../../utils/api/Dashboard';
import { ReactComponent as CalendarIcon } from '../../../assets/icons/icCalendar.svg';
import { ReactComponent as MetricArrowIcon } from '../../../assets/icons/icMetricArrow.svg';
import { ReactComponent as MetricArrowGreenIcon } from '../../../assets/icons/icMetricArrowGreen.svg';
import { ReactComponent as CheckCompletedIcon } from '../../../assets/icons/icCheckCompleted.svg';
import { ReactComponent as InfoCircleIcon } from '../../../assets/icons/icInfoCircle.svg';
import RockieIcon from '../../../assets/icons/rockie.svg';
import { PLAYERS_DASHBOARD_RANGE_OPTIONS } from '../../../utils/constants/dashboardDateRanges';
import { getMonday, formatWeekRangeYear, shiftWeek, isCurrentWeek } from '../../../utils/helpers/dashboardDate';
import { usePlayerDashboardData } from './usePlayerDashboardData';
import type { DashboardPreset } from './usePlayerDashboardData';
import {
  DashboardFiltersBar,
  DashboardFiltersRight,
  RangeTabs,
  RangeTab,
  RangeTabCustomIcon,
} from '../dashboard/styles';
import {
  DashboardWrapper,
  TopRow,
  BottomRow,
  PlayerMetricCard,
  PlayerMetricCardHeader,
  PlayerMetricTitle,
  PlayerMetricInfoTooltipWrap,
  PlayerMetricInfoIcon,
  PlayerMetricTooltipBubble,
  PlayerMetricSingleTile,
  PlayerMetricTileLabel,
  PlayerMetricValueRow,
  PlayerMetricNumber,
  PlayerMetricUnit,
  PlayerMetricIconWrap,
  StatCard,
  StatCardHeader,
  StatLabel,
  StatBody,
  StatMainValue,
  StatDetails,
  StreakBox,
  StreakLabel,
  StreakValue,
  StreakRecommended,
  RecommendedLabel,
  WeeklySection,
  WeeklyHeader,
  WeeklyTitle,
  WeekNav,
  WeekNavBtn,
  WeekListDropdown,
  WeekListScroll,
  WeekListItem,
  TableContainer,
  WeeklyTable,
  TableHead,
  Th,
  Td,
  Tr,
  ThrowingValue,
  CompletedBadge,
  AlertBadge,
  DateRangeBtn,
  DateRangeChevron,
  DateRangeWrap,
  CardPlaceholder,
  LevelSection,
  LevelText,
  LevelBadge,
} from './dashboard.styles';


// ── Player metric card (ArmScore / Total Arm Strength) ───────────────────────

interface WhiteMetricCardProps {
  label: string;
  tooltip: string;
  totalGain: number | null;
  unit: string;
  variant?: 'blue' | 'green';
  loading: boolean;
}

const WhiteMetricCard = ({ label, tooltip, totalGain, unit, variant = 'blue', loading }: WhiteMetricCardProps) => (
  <PlayerMetricCard>
    <PlayerMetricCardHeader>
      <PlayerMetricTitle>{label}</PlayerMetricTitle>
      <PlayerMetricInfoTooltipWrap>
        <PlayerMetricInfoIcon type="button" aria-label={`About ${label}`}>i</PlayerMetricInfoIcon>
        <PlayerMetricTooltipBubble role="tooltip">{tooltip}</PlayerMetricTooltipBubble>
      </PlayerMetricInfoTooltipWrap>
    </PlayerMetricCardHeader>

    {loading ? (
      <CardPlaceholder><Loader height="50px" /></CardPlaceholder>
    ) : (
      <PlayerMetricSingleTile $variant={variant}>
        <PlayerMetricTileLabel>Total Gain</PlayerMetricTileLabel>
        <PlayerMetricValueRow>
          <PlayerMetricNumber>{totalGain !== null ? totalGain.toLocaleString() : '0'}</PlayerMetricNumber>
          <PlayerMetricUnit>{unit}</PlayerMetricUnit>
        </PlayerMetricValueRow>
        <PlayerMetricIconWrap>
          {variant === 'green' ? <MetricArrowGreenIcon /> : <MetricArrowIcon />}
        </PlayerMetricIconWrap>
      </PlayerMetricSingleTile>
    )}
  </PlayerMetricCard>
);

// ── Blue stat card (Testing / Training) ──────────────────────────────────────

interface StatCardProps {
  label: string;
  tooltip: string;
  total: number;
  details: { label: string; value: string | number }[];
  streakWeeks: number;
  streakRequirement: string;
  loading: boolean;
}

const RecommendedIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="7" cy="7" r="6.5" stroke="#0056B2" />
    <path d="M4.5 7L6.5 9L9.5 5.5" stroke="#0056B2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const BlueStatCard = ({ label, tooltip, total, details, streakWeeks, streakRequirement, loading }: StatCardProps) => (
  <StatCard>
    <StatCardHeader>
      <StatLabel>
        {label} <span style={{ fontWeight: 400 }}>TOTAL</span>
      </StatLabel>
      <PlayerMetricInfoTooltipWrap>
        <PlayerMetricInfoIcon
          type="button"
          aria-label={`About ${label}`}
          style={{ background: 'transparent', border: '1.5px solid rgba(255,255,255,0.8)', color: '#fff' }}
        >
          i
        </PlayerMetricInfoIcon>
        <PlayerMetricTooltipBubble role="tooltip">{tooltip}</PlayerMetricTooltipBubble>
      </PlayerMetricInfoTooltipWrap>
    </StatCardHeader>

    {loading ? (
      <CardPlaceholder style={{ color: 'rgba(255,255,255,0.5)' }}><Loader height="50px" /></CardPlaceholder>
    ) : (
      <>
        <StatBody>
          <StatMainValue>{total}</StatMainValue>
          <StatDetails>
            {details.map((d) => (
              <div key={d.label}>
                {d.label}: <span>{d.value}</span>
              </div>
            ))}
          </StatDetails>
        </StatBody>
        <StreakBox>
          <div>
            <StreakLabel>Streak</StreakLabel>
            <StreakValue>{streakWeeks} weeks</StreakValue>
          </div>
          <StreakRecommended>
            <RecommendedLabel>
              <RecommendedIcon />
              Recommended
            </RecommendedLabel>
            {streakRequirement}
          </StreakRecommended>
        </StreakBox>
      </>
    )}
  </StatCard>
);

// ── Weekly Activity table ─────────────────────────────────────────────────────

const API_DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'] as const;
type ApiDayKey = typeof API_DAYS[number];

const DAY_HEADERS: Record<ApiDayKey, string> = {
  MON: 'MON', TUE: 'TUE', WED: 'WED', THU: 'THURS', FRI: 'FRI', SAT: 'SAT', SUN: 'SUN',
};

const CATEGORIES: { label: string; field: keyof PlayerWeeklyDayData }[] = [
  { label: 'Arm Feels', field: 'armFeels' },
  { label: 'Body Feels', field: 'bodyFeels' },
  { label: 'Sleep Quality', field: 'sleepQuality' },
  { label: 'Arm Primer', field: 'armPrimer' },
  { label: 'Fresh Exam', field: 'freshExam' },
  { label: 'Post Exam', field: 'postExam' },
  { label: 'Strength Alerts', field: 'strengthAlerts' },
  { label: 'Recovery Alerts', field: 'recoveryAlerts' },
  { label: 'Fatigue Alerts', field: 'fatigueAlerts' },
  { label: 'Throwing', field: 'throwingType' },
  { label: 'RPE', field: 'rpe' },
  { label: 'Count', field: 'count' },
  { label: 'Custom Training', field: 'customTraining' },
];

const DARK_ROW_FIELDS = new Set<keyof PlayerWeeklyDayData>([
  'armFeels', 'bodyFeels', 'sleepQuality',
  'strengthAlerts', 'recoveryAlerts', 'fatigueAlerts',
  'customTraining',
]);

const renderCell = (value: PlayerWeeklyDayData[keyof PlayerWeeklyDayData], field?: keyof PlayerWeeklyDayData) => {
  if (value === null || value === undefined) return '-';
  if (value === 'Completed') return <CompletedBadge>Completed <CheckCompletedIcon /></CompletedBadge>;
  if (value === 'Normal' || value === 'No Alerts') return <AlertBadge variant="noAlert">No Alerts <CheckCompletedIcon /></AlertBadge>;
  if (value === 'Watch') return <AlertBadge variant="watch">Watch</AlertBadge>;
  if (value === 'Warning') return <AlertBadge variant="warning">Warning</AlertBadge>;
  if (value === 'Medical') return <AlertBadge variant="medical">Medical</AlertBadge>;
  if (field === 'throwingType') return <ThrowingValue>{value}</ThrowingValue>;
  return <>{value}</>;
};

interface WeeklyActivityProps {
  data: PlayerWeeklyActivityData | null;
  currentWeekStart: string;
  onJumpToWeek: (d: Date) => void;
  loading: boolean;
}

const WEEK_LIST_PAGE_SIZE = 8;

const WeeklyActivity = ({ data, currentWeekStart, onJumpToWeek, loading }: WeeklyActivityProps) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [visibleWeeks, setVisibleWeeks] = useState(WEEK_LIST_PAGE_SIZE);

  const weekOptions = Array.from({ length: visibleWeeks }, (_, i) => shiftWeek(getMonday(), -i));

  const openWeekList = () => {
    setVisibleWeeks(WEEK_LIST_PAGE_SIZE);
    setPickerOpen((o) => !o);
  };

  const handleWeekListScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 40) {
      setVisibleWeeks((c) => c + WEEK_LIST_PAGE_SIZE);
    }
  };

  return (
  <WeeklySection>
    <WeeklyHeader>
      <WeeklyTitle>
        Weekly Activity
        <span style={{ color: '#64748b', cursor: 'default', display: 'flex' }}>
          <InfoCircleIcon />
        </span>
      </WeeklyTitle>
      <WeekNav>
        <WeekNavBtn
          type="button"
          onClick={() => onJumpToWeek(new Date(shiftWeek(currentWeekStart, -1)))}
        >
          <GoChevronLeft size={19} /> Previous
        </WeekNavBtn>

        <DateRangeWrap>
          <DateRangeBtn onClick={openWeekList}>
            {formatWeekRangeYear(currentWeekStart)}
            <DateRangeChevron><GoChevronDown size={19} /></DateRangeChevron>
          </DateRangeBtn>

          {pickerOpen && (
            <WeekListDropdown>
              <WeekListScroll onScroll={handleWeekListScroll}>
                {weekOptions.map((week) => (
                  <WeekListItem
                    key={week}
                    $active={week === currentWeekStart}
                    onClick={() => { onJumpToWeek(new Date(week)); setPickerOpen(false); }}
                  >
                    {formatWeekRangeYear(week)}
                  </WeekListItem>
                ))}
              </WeekListScroll>
            </WeekListDropdown>
          )}
        </DateRangeWrap>

        <WeekNavBtn
          type="button"
          disabled={isCurrentWeek(currentWeekStart)}
          onClick={() => onJumpToWeek(new Date(shiftWeek(currentWeekStart, 1)))}
        >
          Next <GoChevronRight size={19} />
        </WeekNavBtn>
      </WeekNav>
    </WeeklyHeader>

    <TableContainer>
      <WeeklyTable>
        <TableHead>
          <tr>
            <Th first>Category</Th>
            {API_DAYS.map((d) => <Th key={d}>{DAY_HEADERS[d]}</Th>)}
          </tr>
        </TableHead>
        <tbody>
          {CATEGORIES.map((cat) => (
            <Tr key={cat.field} $dark={DARK_ROW_FIELDS.has(cat.field)}>
              <Td first>{cat.label}</Td>
              {API_DAYS.map((day) => {
                if (loading) return <Td key={day}><Loader height="20px" width="100%" /></Td>;
                const dayData = data?.days?.[day];
                const val = dayData ? dayData[cat.field] : null;
                return <Td key={day}>{renderCell(val, cat.field)}</Td>;
              })}
            </Tr>
          ))}
        </tbody>
      </WeeklyTable>
    </TableContainer>
  </WeeklySection>
  );
};

// ── Tooltips text ─────────────────────────────────────────────────────────────

const TOOLTIPS = {
  armScore: 'Your ArmScore reflects the change in relative arm strength (arm strength divided by bodyweight) from your earliest to most recent exam within the selected date range.',
  totalArmStrength: 'Your Arm Strength shows how much your total strength has gained across all key movements (IR, ER, Scaption, and Grip) during the selected time period.',
  testing: 'This shows the total number of ArmCare exams completed during the selected date range, including Fresh Exams, Post Exams, and Arm Primers.',
  training: 'This reflects the total number of training sessions completed, including Strength, Hybrid, and Off Days.',
};

// ── Main component ────────────────────────────────────────────────────────────

interface PlayerDashboardProps {
  playerId?: string;
}

const PlayerDashboard = ({ playerId }: PlayerDashboardProps = {}) => {
  const {
    rangePreset, setRangePreset,
    customStart, setCustomStart,
    customEnd, setCustomEnd,
    setCommittedStart,
    setCommittedEnd,
    currentWeekStart, setCurrentWeekStart,
    summaryData,
    weeklyData,
    loadingSummary,
    loadingWeekly,
  } = usePlayerDashboardData(playerId);

  const activeRange = PLAYERS_DASHBOARD_RANGE_OPTIONS.find(o => o.id === rangePreset) ?? PLAYERS_DASHBOARD_RANGE_OPTIONS[0];

  const armScore = summaryData?.armScore;
  const armStrength = summaryData?.totalArmStrength;
  const testing = summaryData?.testing;
  const training = summaryData?.training;

  return (
    <FluidContainer overflow={false}>
      <DashboardWrapper>
        {/* ── Filters ── */}
        <DashboardFiltersBar>
          {/* <LevelSection>
            <LevelText>Level</LevelText>
            <span style={{ color: '#94a3b8', cursor: 'default', display: 'flex' }}>
              <InfoCircleIcon />
            </span>
            <LevelBadge>
              <img src={RockieIcon} alt="Rookie" style={{ width: 16, height: 16 }} />
              Rookie
            </LevelBadge>
          </LevelSection> */}
          <DashboardFiltersRight>
            <RangeTabs>
              {PLAYERS_DASHBOARD_RANGE_OPTIONS.map(option => (
                <RangeTab
                  key={option.id}
                  type="button"
                  active={option.id === activeRange.id}
                  isCustom={option.id === 'custom'}
                  onClick={() => setRangePreset(option.id as DashboardPreset)}
                >
                  {option.title}
                  {option.id === 'custom' && (
                    <RangeTabCustomIcon active={option.id === activeRange.id}>
                      <CalendarIcon />
                    </RangeTabCustomIcon>
                  )}
                </RangeTab>
              ))}
            </RangeTabs>
            {rangePreset === 'custom' && (
              <InputDate
                showRange={true}
                closeIcon={false}
                dropdownPicker={true}
                date={customStart || new Date()}
                endDate={customEnd || new Date()}
                setDate={(date: Date) => { setCustomStart(date); setCustomEnd(null); setCommittedStart(date); }}
                setEndDate={(date: Date) => { setCustomEnd(date); setCommittedEnd(date); }}
                minEndDate={customStart || undefined}
              />
            )}
          </DashboardFiltersRight>
        </DashboardFiltersBar>

        {/* ── Top metric cards ── */}
        <TopRow>
          <WhiteMetricCard
            label="ARMSCORE"
            tooltip={TOOLTIPS.armScore}
            totalGain={armScore?.totalGain ?? null}
            unit="pts"
            loading={loadingSummary}
          />
          <WhiteMetricCard
            label="TOTAL ARM STRENGTH"
            tooltip={TOOLTIPS.totalArmStrength}
            totalGain={armStrength?.totalGain ?? null}
            unit="lbs"
            variant="green"
            loading={loadingSummary}
          />
        </TopRow>

        {/* ── Stat cards ── */}
        <BottomRow>
          <BlueStatCard
            label="TESTING"
            tooltip={TOOLTIPS.testing}
            total={testing?.total ?? 0}
            details={[
              { label: 'Fresh Exams', value: testing?.breakdown.fresh ?? 0 },
              { label: 'Post Exams', value: testing?.breakdown.post ?? 0 },
              { label: 'Arm Primer', value: testing?.breakdown.armPrimer ?? 0 },
            ]}
            streakWeeks={testing?.streak.weeks ?? 0}
            streakRequirement={testing?.streak.requirement ?? ''}
            loading={loadingSummary}
          />
          <BlueStatCard
            label="TRAINING"
            tooltip={TOOLTIPS.training}
            total={training?.total ?? 0}
            details={[
              { label: 'Strength', value: training?.breakdown.strength ?? 0 },
              { label: 'Hybrid', value: training?.breakdown.hybrid ?? 0 },
              { label: 'Off Day', value: training?.breakdown.offDay ?? 0 },
            ]}
            streakWeeks={training?.streak.weeks ?? 0}
            streakRequirement={training?.streak.requirement ?? ''}
            loading={loadingSummary}
          />
        </BottomRow>

        {/* ── Weekly Activity ── */}
        <WeeklyActivity
          data={weeklyData}
          currentWeekStart={currentWeekStart}
          onJumpToWeek={(d) => setCurrentWeekStart(getMonday(d))}
          loading={loadingWeekly}
        />
      </DashboardWrapper>
    </FluidContainer>
  );
};

export default PlayerDashboard;
